import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import type {
  VideoListItem,
  VideoDetail,
  VideoSettings,
  TuningData,
  ResolvedCaption,
  VideoTagState,
  TagSuggestion,
  TagSource,
  SuggestionDecisionResponse,
} from '../types/videos';

const STALE_TIME_MS = 30_000;
// Keep the list query alive in the cache for 30 minutes so back-navigation
// from a video detail page renders instantly instead of re-showing the
// full-page spinner (default gcTime is 5 minutes, after which the cached
// list is garbage-collected while the operator sits on a detail page).
const LIST_GC_TIME_MS = 30 * 60_000;

export const videoKeys = {
  all: ['videos'] as const,
  list: () => [...videoKeys.all, 'list'] as const,
  detailPrefix: (stem: string) => [...videoKeys.all, 'detail', stem] as const,
  detail: (stem: string, lang?: string | null) =>
    [...videoKeys.all, 'detail', stem, lang ?? 'original'] as const,
  tagState: (stem: string) => [...videoKeys.detailPrefix(stem), 'tag-state'] as const,
  jobs: () => [...videoKeys.all, 'jobs'] as const,
  settings: () => [...videoKeys.all, 'settings'] as const,
  tuning: () => [...videoKeys.all, 'tuning'] as const,
  tags: () => [...videoKeys.all, 'tags'] as const,
  captionCoverage: () => [...videoKeys.all, 'caption-coverage'] as const,
};

export function useVideosQuery() {
  return useQuery<{ videos: VideoListItem[] }>({
    queryKey: videoKeys.list(),
    queryFn: api.getVideos,
    staleTime: STALE_TIME_MS,
    gcTime: LIST_GC_TIME_MS,
  });
}

export function useVideoQuery(stem: string, lang?: string | null) {
  return useQuery<VideoDetail>({
    queryKey: videoKeys.detail(stem, lang),
    queryFn: () => api.getVideo(stem, lang),
    staleTime: STALE_TIME_MS,
    enabled: Boolean(stem),
    placeholderData: keepPreviousData,
  });
}

export function useVideoJobsQuery(options?: { refetchInterval?: number | false; enabled?: boolean }) {
  return useQuery<{ jobs: any[]; translation_jobs: import('../types/videos').TranslationJob[] }>({
    queryKey: videoKeys.jobs(),
    queryFn: api.getVideoJobs,
    staleTime: 2_000,
    refetchInterval: options?.refetchInterval ?? false,
    enabled: options?.enabled ?? true,
  });
}

export function useVideoSettingsQuery() {
  return useQuery<VideoSettings>({
    queryKey: videoKeys.settings(),
    queryFn: api.getVideoSettings,
    staleTime: STALE_TIME_MS,
  });
}

export function useUpdateVideoSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.updateVideoSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.settings() });
      queryClient.invalidateQueries({ queryKey: videoKeys.tuning() });
    },
  });
}

export function useVideoTuningQuery() {
  return useQuery<TuningData>({
    queryKey: videoKeys.tuning(),
    queryFn: api.getVideoTuning,
    staleTime: STALE_TIME_MS,
  });
}

export function useVideoTagsQuery() {
  return useQuery<{ tags: string[] }>({
    queryKey: videoKeys.tags(),
    queryFn: api.getVideoTags,
    staleTime: STALE_TIME_MS,
  });
}

/**
 * Per-video tag state (manual + accepted tags with sources, plus pending
 * suggestions) from the extended GET /api/videos/:stem/tags. The tag panel
 * renders from this — never infers tag provenance from other payloads.
 */
export function useVideoTagStateQuery(stem: string) {
  return useQuery<VideoTagState>({
    queryKey: videoKeys.tagState(stem),
    queryFn: () => api.getVideoTagState(stem),
    staleTime: STALE_TIME_MS,
    enabled: Boolean(stem),
  });
}

export interface SuggestionDecisionVariables {
  suggestion: TagSuggestion;
  decision: 'accept' | 'reject';
}

/**
 * Accept or reject one pending tag suggestion. Optimistically applies the
 * outcome to the tag-state cache (accept-add promotes the tag to an accepted
 * tag, accept-remove deletes it, reject just drops the proposal) and rolls
 * everything back on error. Rejections are recorded server-side — dismissing
 * is data for the model, not a no-op.
 */
export function useDecideSuggestionMutation(stem: string) {
  const queryClient = useQueryClient();
  return useMutation<
    SuggestionDecisionResponse,
    Error,
    SuggestionDecisionVariables,
    { snapshot: VideoTagState | undefined }
  >({
    mutationFn: ({ suggestion, decision }) => api.decideSuggestion(stem, suggestion.id, decision),
    onMutate: async ({ suggestion, decision }) => {
      await queryClient.cancelQueries({ queryKey: videoKeys.tagState(stem) });
      const snapshot = queryClient.getQueryData<VideoTagState>(videoKeys.tagState(stem));

      queryClient.setQueryData<VideoTagState>(videoKeys.tagState(stem), (old) => {
        if (!old) return old;
        const pending_suggestions = old.pending_suggestions.filter((s) => s.id !== suggestion.id);
        if (decision === 'reject') return { ...old, pending_suggestions };

        if (suggestion.action === 'add') {
          const tags = [...old.tags, suggestion.tag].sort((a, b) => a.localeCompare(b));
          return {
            ...old,
            tags,
            tag_sources: { ...old.tag_sources, [suggestion.tag]: 'accepted_suggestion' },
            pending_suggestions,
          };
        }
        const tag_sources = { ...old.tag_sources };
        delete tag_sources[suggestion.tag];
        return {
          ...old,
          tags: old.tags.filter((t) => t !== suggestion.tag),
          tag_sources,
          pending_suggestions,
        };
      });

      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(videoKeys.tagState(stem), context.snapshot);
      }
    },
    onSuccess: (data) => {
      queryClient.setQueryData<VideoTagState>(videoKeys.tagState(stem), (old) =>
        old ? { ...old, tags: data.tags, tag_sources: data.tag_sources } : old,
      );
      queryClient.invalidateQueries({ queryKey: videoKeys.tagState(stem) });
      // An accepted removal can delete a manual tag, which feeds the list and
      // detail payloads too.
      queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem) });
      queryClient.invalidateQueries({ queryKey: videoKeys.list() });
    },
  });
}

export function useTranscribeVideoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stem, force }: { stem: string; force?: boolean }) => api.transcribeVideo(stem, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.jobs() });
    },
  });
}

export function useTranscribeBackfillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.transcribeBackfill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.jobs() });
    },
  });
}

export function useTranslateVideoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stem, target_lang, force }: { stem: string; target_lang?: string; force?: boolean }) =>
      api.translateVideo(stem, { target_lang, force }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.jobs() });
    },
  });
}

export function useTranslateBackfillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.translateBackfill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.list() });
      queryClient.invalidateQueries({ queryKey: videoKeys.jobs() });
    },
  });
}

export function useUpdateVideoNotesMutation(stem: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => api.updateVideoNotes(stem, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem) });
    },
  });
}

export function useUpdateVideoTagsMutation(stem: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tags: string[]) => api.updateVideoTags(stem, tags),
    onMutate: async (manualTags) => {
      await queryClient.cancelQueries({ queryKey: videoKeys.detailPrefix(stem) });
      await queryClient.cancelQueries({ queryKey: videoKeys.list() });

      const listSnapshot = queryClient.getQueryData<{ videos: VideoListItem[] }>(videoKeys.list());
      const detailSnapshots = queryClient.getQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) });
      const tagStateSnapshot = queryClient.getQueryData<VideoTagState>(videoKeys.tagState(stem));

      const patchList = (old: { videos: VideoListItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          videos: old.videos.map((v) => (v.stem === stem ? { ...v, tags: manualTags } : v)),
        };
      };
      const patchDetail = (old: VideoDetail | undefined) => {
        // videoKeys.tagState nests under detailPrefix, so this patcher also
        // sees VideoTagState-shaped entries. Skip them — clobbering their
        // tags with the manual-only array would hide accepted tags.
        if (!old || 'pending_suggestions' in old) return old;
        return { ...old, tags: manualTags };
      };
      // The tag panel renders from the tag-state entry: manual rows are
      // replaced, accepted tags carry over untouched.
      const patchTagState = (old: VideoTagState | undefined) => {
        if (!old) return old;
        const accepted = old.tags.filter((t) => old.tag_sources[t] === 'accepted_suggestion');
        const tag_sources: Record<string, TagSource> = {};
        for (const t of manualTags) tag_sources[t] = 'manual';
        for (const t of accepted) tag_sources[t] = 'accepted_suggestion';
        return {
          ...old,
          tags: [...manualTags, ...accepted].sort((a, b) => a.localeCompare(b)),
          tag_sources,
        };
      };

      queryClient.setQueryData(videoKeys.list(), patchList);
      queryClient.setQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) }, patchDetail);
      queryClient.setQueryData<VideoTagState>(videoKeys.tagState(stem), patchTagState);

      return { listSnapshot, detailSnapshots, tagStateSnapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.listSnapshot !== undefined) {
        queryClient.setQueryData(videoKeys.list(), context.listSnapshot);
      }
      context?.detailSnapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
      if (context?.tagStateSnapshot !== undefined) {
        queryClient.setQueryData(videoKeys.tagState(stem), context.tagStateSnapshot);
      }
    },
    onSuccess: (data) => {
      const patchList = (old: { videos: VideoListItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          videos: old.videos.map((v) => (v.stem === stem ? { ...v, tags: data.tags } : v)),
        };
      };
      const patchDetail = (old: VideoDetail | undefined) => {
        if (!old || 'pending_suggestions' in old) return old;
        return { ...old, tags: data.tags };
      };
      const patchTagState = (old: VideoTagState | undefined) => {
        if (!old) return old;
        const accepted = old.tags.filter((t) => old.tag_sources[t] === 'accepted_suggestion');
        const tag_sources: Record<string, TagSource> = {};
        for (const t of data.tags) tag_sources[t] = 'manual';
        for (const t of accepted) tag_sources[t] = 'accepted_suggestion';
        return {
          ...old,
          tags: [...data.tags, ...accepted].sort((a, b) => a.localeCompare(b)),
          tag_sources,
        };
      };

      queryClient.setQueryData(videoKeys.list(), patchList);
      queryClient.setQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) }, patchDetail);
      queryClient.setQueryData<VideoTagState>(videoKeys.tagState(stem), patchTagState);

      queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem) });
      queryClient.invalidateQueries({ queryKey: videoKeys.list() });
      queryClient.invalidateQueries({ queryKey: videoKeys.tuning() });
      queryClient.invalidateQueries({ queryKey: videoKeys.tags() });
    },
  });
}

export function useCaptionCoverageQuery() {
  return useQuery<{ total: number; with_caption: number; missing: { stem: string; title: string }[] }>({
    queryKey: videoKeys.captionCoverage(),
    queryFn: api.getCaptionCoverage,
    staleTime: STALE_TIME_MS,
  });
}

export function useUpdateVideoCaptionMutation(stem: string) {
  const queryClient = useQueryClient();
  return useMutation<ResolvedCaption, Error, string>({
    mutationFn: (body: string) => api.updateVideoCaption(stem, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem) });
      queryClient.invalidateQueries({ queryKey: videoKeys.list() });
      queryClient.invalidateQueries({ queryKey: videoKeys.captionCoverage() });
    },
  });
}

export function useUpdateVideoTitleMutation(stem: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string) => api.updateVideoTitle(stem, title),
    onMutate: async (title) => {
      await queryClient.cancelQueries({ queryKey: videoKeys.detailPrefix(stem) });
      await queryClient.cancelQueries({ queryKey: videoKeys.list() });

      const listSnapshot = queryClient.getQueryData<{ videos: VideoListItem[] }>(videoKeys.list());
      const detailSnapshots = queryClient.getQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) });

      const patchList = (old: { videos: VideoListItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          videos: old.videos.map((v) =>
            v.stem === stem
              ? {
                  ...v,
                  title,
                  title_source: 'manual' as const,
                }
              : v
          ),
        };
      };
      const patchDetail = (old: VideoDetail | undefined) => {
        if (!old) return old;
        return {
          ...old,
          title,
          title_source: 'manual' as const,
        };
      };

      queryClient.setQueryData(videoKeys.list(), patchList);
      queryClient.setQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) }, patchDetail);

      return { listSnapshot, detailSnapshots };
    },
    onError: (_err, _vars, context) => {
      if (context?.listSnapshot !== undefined) {
        queryClient.setQueryData(videoKeys.list(), context.listSnapshot);
      }
      context?.detailSnapshots?.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSuccess: (data) => {
      const patchList = (old: { videos: VideoListItem[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          videos: old.videos.map((v) =>
            v.stem === stem
              ? {
                  ...v,
                  title: data.title,
                  title_source: data.title_source,
                }
              : v
          ),
        };
      };
      const patchDetail = (old: VideoDetail | undefined) => {
        if (!old) return old;
        return {
          ...old,
          title: data.title,
          title_source: data.title_source,
        };
      };

      queryClient.setQueryData(videoKeys.list(), patchList);
      queryClient.setQueriesData<VideoDetail>({ queryKey: videoKeys.detailPrefix(stem) }, patchDetail);

      queryClient.invalidateQueries({ queryKey: videoKeys.detailPrefix(stem) });
      queryClient.invalidateQueries({ queryKey: videoKeys.list() });
    },
  });
}
